/** Storage before the storage client exists: every method waits for `loadSupabaseExtras` and delegates; only
 * `getPublicUrl` is synchronous in storage-js, so it is built here exactly as storage-js 2.97.0 builds it
 * (bucket + path without leading slashes, `encodeURI`). With options it needs the real client. */
export interface StorageSource {
    from(bucket: string): Record<string, unknown>;
}

export function deferredStorage(
    url: string,
    load: () => Promise<StorageSource>,
    loaded: () => StorageSource | null,
): { from(bucket: string): Record<string, unknown> } {
    const base = url.replace(/\/+$/, '');
    return {
        from(bucket: string) {
            return new Proxy({} as Record<string, unknown>, {
                get(_target, method) {
                    if (typeof method !== 'string' || method === 'then') return undefined;
                    if (method === 'getPublicUrl') {
                        return (path: string, options?: unknown) => {
                            const ready = loaded();
                            if (ready) {
                                const api = ready.from(bucket);
                                return Reflect.apply(api.getPublicUrl as (p: string, o?: unknown) => unknown, api, [path, options]);
                            }
                            if (options !== undefined) {
                                throw new Error('[supabase-lite] getPublicUrl with options needs loadSupabaseExtras() first');
                            }
                            const clean = path.replace(/^\/+/, '');
                            return { data: { publicUrl: encodeURI(`${base}/storage/v1/object/public/${bucket}/${clean}`) } };
                        };
                    }
                    return async (...args: unknown[]) => {
                        let api: Record<string, unknown>;
                        try {
                            api = (await load()).from(bucket);
                        } catch (error: unknown) {
                            /* Same shape storage-js returns on a network failure: callers keep their own error path
                               (uploadReceipt → Result.fail) instead of a rejected promise in the payment flow. */
                            return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
                        }
                        const fn = api[method];
                        if (typeof fn !== 'function') throw new Error(`[supabase-lite] storage has no "${method}"`);
                        return Reflect.apply(fn as (...a: unknown[]) => unknown, api, args);
                    };
                },
            });
        },
    };
}
